/* eslint-disable camelcase */
import { useEffect, useState } from 'react';
import { Box, CircularProgress, Drawer, Fab, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { CancelPresentationRounded, ChevronLeft, ChevronRight, KeyboardArrowUp } from '@mui/icons-material';
import { columns, customColumns } from './table_config';
import { useSelector } from 'react-redux';
import CanvasJSReact from '../../../ui/canvasjs/canvasjs.react';
import { ROLLOVER_DISPLACEMENT_TYPE_SECONDARY } from '../../../../utils/constants';
import { DISPLACEMENT_TYPE_COLORS, HYDRAULICS_CHART_COLORS } from '../../../../shared/config/hydraulics_constants';
import Datagrid from '../../../ui/Datagrid';
import styles from './styles';
import { isMobile } from 'react-device-detect';
import { COLORS } from '../../../../shared/config/colors';
import { getOptimalInterval } from '../../../../utils/getOptimalInterval';
import { getDepthValueFromField } from '../../../../utils/depthSummaryExtractor';

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

const FeaturedWellDisplacementChart = ({
  data,
  units,
}) => {
  const classes = styles();
  const [tableRow, setTableRows] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const hydraulicsLanding = useSelector(state => state.hydraulicsLanding);
  const displacement = data;
  const displacementUnits = units.displacement.results;
  const [titleHeader] = useState('Displacement');
  const [plotForPrimary, setPlotForPrimary] = useState('Pressure');
  const [plotForSecondary, setPlotForSecondary] = useState('Hole Depth');
  const [showMobileChartTable, setShowMobileChartTable] = useState(false);
  const [secondaryDepthField, setSecondaryDepthField] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [chartOptions, setChartOptions] = useState({});
  const sbpField = 'connection_sbp';

  useEffect(() => {
    if (!hydraulicsLanding?.loading) {
      const displacementData = displacement?.results;
      if (!displacementData.length) {
        setTableRows([]);
        return;
      }

      const modifiedRecords = displacement.results.map((record) => ({
        ...record,
        _id: Math.random().toString(36).substring(2, 10)
      }));

      setTableRows(modifiedRecords);
      setShowTable(true);
      if (plotForPrimary === 'Pressure') {
        setChartData(displacement?.results);
      } else {
        const selected = secondaryDepthField.find(
          (f) => f.label === plotForSecondary
        );
        if (selected?.field?.length) {
          const [esdKey, ecdKey] = selected.field;
          const esdData = [];
          const ecdData = [];
          displacementData.forEach((r) => {
            const strokes = r?.strokes ?? 0;

            const esdValue = getDepthValueFromField(r, esdKey) ?? 0;
            const ecdValue = getDepthValueFromField(r, ecdKey) ?? 0;

            esdData.push({
              x: strokes,
              y: esdValue,
            });

            ecdData.push({
              x: strokes,
              y: ecdValue,
            });
          });
          setChartData([
            { name: `ESD @ ${plotForSecondary} (${displacementUnits?.bh_esd?.label})`, data: esdData },
            { name: `ECD @ ${plotForSecondary} (${displacementUnits?.bh_esd?.label})`, data: ecdData }
          ]);
        }
      }
    } else {
      setShowTable(false);
    }
  }, [data, hydraulicsLanding?.loading, plotForPrimary, plotForSecondary]);

  const secondaryField = secondaryDepthField.find(
    (type) => type.label === plotForSecondary
  );

  useEffect(() => {
    const fields = displacement?.results?.[0];
    let secondary_fields = [
      ...ROLLOVER_DISPLACEMENT_TYPE_SECONDARY,
    ];
    const esdEcdKeys = Object.keys(fields || {}).filter(
      (key) => key.endsWith('_esd') || key.endsWith('_ecd')
    );
    const omitList = [
      'icp_esd',
      'icp_ecd',
      'lp_esd',
      'lp_ecd',
      'bh_esd',
      'bh_ecd',
    ];
    const filteredKeys = esdEcdKeys.filter(
      (key) => !omitList.includes(key)
    );

    const groupedFields = filteredKeys.reduce((acc, key) => {
      const prefix = key.replace(/_(esd|ecd)$/, '');
      if (!acc[prefix]) acc[prefix] = [];
      acc[prefix].push(key);
      return acc;
    }, {});

    const secondaryFieldsMap = new Map(
      secondary_fields.map(item => [
        item.label.toLowerCase(),
        item
      ])
    );

    Object.entries(groupedFields).forEach(([prefix, fieldArray]) => {
      const formattedLabel = prefix.replace(/_/g, ' ');
      const key = formattedLabel.toLowerCase();
      const existing = secondaryFieldsMap.get(key);

      secondaryFieldsMap.set(key, {
        label: existing?.label ?? formattedLabel,
        field: fieldArray,
      });
    });

    secondary_fields = Array.from(secondaryFieldsMap.values());
    setSecondaryDepthField(secondary_fields);
  }, [data]);

  const generateViewportMinMax = () => {
    const allDepthPoints = chartData.flatMap(item => item.data ?? []);
    const minMaxData = [...allDepthPoints];
    const keysToCheck = ['y'];

    const allValues = [];

    // Collect all values from the specified keys
    keysToCheck.forEach(key => {
      minMaxData.forEach(obj => {
        if (key in obj) {
          allValues.push(obj[key]);
        }
      });
    });

    // Calculate the overall lowest and highest values
    const overallLowest = Math.min(...allValues);
    const overallHighest = Math.max(...allValues);

    const results = {
      lowest: overallLowest,
      highest: overallHighest,
    };
    return results;
  };

  useEffect(() => {
    if (!chartData?.length) return;

    const options =
      plotForPrimary === 'Pressure'
        ? {
            animationEnabled: !isMobile,
            theme: 'light2',
            backgroundColor: '#FFF',
            height: isMobile ? 620 : 900,
            legend: {
              fontSize: isMobile ? 14 : 18,
              maxWidth: 300
            },
            axisY: {
              title: 'Strokes',
              includeZero: true,
              reversed: true,
              titleFontSize: isMobile ? 14 : 22,
              labelFontSize: isMobile ? 12 : 16,
              gridThickness: 1,
            },
            axisX: {
              title: `Pressure (${displacementUnits?.sbp?.label})`,
              minimum: 0,
              titleFontSize: isMobile ? 14 : 22,
              labelFontSize: isMobile ? 12 : 16,
              gridThickness: 1,
            },
            axisX2: {
              title: `Flow Rate (${displacementUnits?.flow_rate?.label})`,
              titleFontSize: isMobile ? 14 : 22,
              labelFontSize: isMobile ? 12 : 16,
              minimum: 0,
              gridThickness: 1,
              labelPlacement: 'outside',
              labelFormatter: function (e) {
                return Math.round(e.value);
              },
            },
            data: [
              {
                type: 'line',
                name: `Dynamic SBP (${displacementUnits?.sbp?.label})`,
                axisXType: 'primary',
                showInLegend: true,
                lineThickness: 1,
                color: HYDRAULICS_CHART_COLORS.DISPLACEMENT_SBP,
                toolTipContent: `Strokes: {y} <br/> Dynamic SBP: {x} ${displacementUnits?.sbp?.label}`,
                dataPoints: displacement?.results?.map((record) => ({
                  x: record.sbp,
                  y: record.strokes,
                })) || [],
              },
              {
                type: 'line',
                name: `Connection SBP (${displacementUnits?.connection_sbp?.label})`,
                axisXType: 'primary',
                showInLegend: true,
                lineThickness: 1,
                color: HYDRAULICS_CHART_COLORS.DISPLACEMENT_CONNECTION_SBP,
                toolTipContent: `Strokes: {y} <br/> Connection SBP: {x} ${displacementUnits?.connection_sbp?.label}`,
                dataPoints: displacement?.results?.map((record) => ({
                  x: record.connection_sbp,
                  y: record.strokes,
                })) || [],
              },
              {
                type: 'line',
                axisXType: 'secondary',
                name: `Flow Rate (${displacementUnits?.flow_rate?.label})`,
                showInLegend: true,
                lineThickness: 1,
                color: HYDRAULICS_CHART_COLORS.DISPLACEMENT_FLOW_RATE,
                toolTipContent: `Strokes: {y} <br/>Flow Rate: {x} ${displacementUnits?.flow_rate?.label}`,
                dataPoints: displacement?.results?.map((record) => ({
                  x: record.flow_rate,
                  y: record.strokes,
                })) || [],
              },
            ],
          }
        : {
            animationEnabled: !isMobile,
            theme: 'light2',
            backgroundColor: '#FFF',
            height: isMobile ? 620 : 900,
            legend: {
              fontSize: isMobile ? 14 : 18,
              maxWidth: 300
            },
            axisY: {
              title: 'Strokes',
              includeZero: true,
              reversed: true,
              titleFontSize: isMobile ? 14 : 22,
              labelFontSize: isMobile ? 12 : 16,
              gridThickness: 1,
            },
            axisX: {
              title: `EMW @ ${plotForSecondary} (${displacementUnits?.bh_ecd?.label ?? ''})`,
              interval: getOptimalInterval(generateViewportMinMax()?.lowest, generateViewportMinMax()?.highest, 10),
              titleFontSize: isMobile ? 14 : 22,
              labelFontSize: isMobile ? 12 : 16,
              gridThickness: 1,
            },
            axisX2: {
              title: `Flow Rate (${displacementUnits?.flow_rate?.label})`,
              titleFontSize: isMobile ? 14 : 22,
              labelFontSize: isMobile ? 12 : 16,
              minimum: 0,
              gridThickness: 1,
              labelPlacement: 'outside',
              labelFormatter: function (e) {
                return Math.round(e.value);
              },
            },
            data: [
              ...(Array.isArray(chartData) && chartData.length > 0
                ? chartData.map((dataset, i) => ({
                  type: 'line',
                  name: dataset?.name ?? `Series ${i + 1}`,
                  lineThickness: 2,
                  showInLegend: true,
                  color:
                  i === 0
                    ? DISPLACEMENT_TYPE_COLORS.ESD
                    : DISPLACEMENT_TYPE_COLORS.ECD,
                  toolTipContent: `Strokes: {y} <br/> ${dataset?.name ?? ''}: {x} ${displacementUnits?.bh_ecd?.label ?? ''
                  }`,
                  dataPoints:
                  dataset?.data?.map?.((d) => ({
                    x: d?.y ?? 0, // EMW value
                    y: d?.x ?? 0, // Strokes
                  })) ?? [],
                }))
                : []),
              {
                type: 'line',
                axisXType: 'secondary',
                name: `Flow Rate (${displacementUnits?.flow_rate?.label})`,
                showInLegend: true,
                lineThickness: 1,
                color: HYDRAULICS_CHART_COLORS.DISPLACEMENT_FLOW_RATE,
                toolTipContent: `Strokes: {y} <br/>Flow Rate: {x} ${displacementUnits?.flow_rate?.label}`,
                dataPoints: displacement?.results?.map((record) => ({
                  x: record.flow_rate,
                  y: record.strokes,
                })) || [],
              },
            ],
          };
    setChartOptions(options);
  }, [chartData, plotForPrimary, plotForSecondary, displacement]);

  const ChartComponent = () => {
    return (
      <CanvasJSChart options={chartOptions} />
    );
  };

  const TableFilterAndDetailsComponent = () => {
    if (isMobile) {
      return (
        <>
          <Box id='back-to-top' />
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ position: 'sticky', top: 0, background: COLORS.WHITE }}
          >
            <Box sx={{ p: 1 }} display="flex" alignItems="center">
              <FormControl
                fullWidth
                sx={{
                  bgcolor: 'transparent',
                  width: { xs: '130px', sm: '140px', md: '160px' },
                  height: { xs: '36px', sm: '40px', md: '48px' },
                }}
              >
                <InputLabel
                  id="primary-filter-label"
                  sx={{
                    fontSize: { xs: '12px', sm: '14px', md: '16px' },
                  }}
                >
                  Type
                </InputLabel>
                <Select
                  labelId="primary-filter-label"
                  id="primary-filter"
                  label="Type"
                  value={plotForPrimary}
                  onChange={(e) => {
                    setPlotForPrimary(e.target.value);
                  }}
                  sx={{
                    fontWeight: 'bold',
                    fontSize: { xs: '12px', sm: '14px', md: '18px' },
                    py: { xs: 0.2, sm: 0.5, md: 1 },
                    height: { xs: '32px', sm: '40px', md: '48px' },
                    background: 'unset',
                  }}
                >
                  {['Pressure', 'EMW'].map((item, idItem) => (
                    <MenuItem
                      key={`item_${item}_${idItem}`}
                      value={item}
                      sx={{
                        fontSize: { xs: '12px', sm: '14px', md: '18px' },
                        fontWeight: 'bold',
                        color: item?.color,
                      }}
                    >
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {plotForPrimary === 'EMW' && (
                <>
                  <Typography sx={{ mx: 1, fontWeight: 'bold' }}>at</Typography>

                  <FormControl
                    fullWidth
                    sx={{
                      bgcolor: 'transparent',
                      width: { xs: '130px', sm: '140px', md: '160px' },
                      height: { xs: '36px', sm: '40px', md: '48px' },
                    }}
                  >
                    <InputLabel
                      id="secondary-filter-label"
                      sx={{
                        fontSize: { xs: '12px', sm: '14px', md: '16px' },
                      }}
                    >
                      Depth
                    </InputLabel>
                    <Select
                      labelId="secondary-filter-label"
                      id="secondary-filter"
                      label="Depth"
                      value={plotForSecondary}
                      onChange={(e) => {
                        setPlotForSecondary(e.target.value);
                      }}
                      sx={{
                        fontWeight: 'bold',
                        fontSize: { xs: '12px', sm: '14px', md: '18px' },
                        py: { xs: 0.2, sm: 0.5, md: 1 },
                        height: { xs: '32px', sm: '40px', md: '48px' },
                        background: 'unset',
                      }}
                    >
                      {secondaryDepthField.map((item, idItem) => (
                        <MenuItem
                          key={`item_${item}_${idItem}`}
                          value={item.label}
                          sx={{
                            fontSize: { xs: '12px', sm: '14px', md: '18px' },
                            fontWeight: 'bold',
                            color: item?.color,
                          }}
                        >
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl> </>
              )
              }
            </Box>

            <IconButton
              onClick={() => {
                setShowMobileChartTable(!showTable);
              }}
              size="small"
            >
              <CancelPresentationRounded fontSize="large" />
            </IconButton>
          </Grid>
        </>
      );
    }

    return (
      <Box display="flex" justifyContent="flex-start" marginLeft={'1%'}>
        <Grid item xs={3}>
          <Box
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
            width="400px"
            gap={2}
          >
            {/* Type Dropdown */}
            <Grid item>
              <FormControl
                fullWidth
                sx={{ bgcolor: 'transparent', width: '160px' }}
              >
                <InputLabel id="filter-input-label">Type</InputLabel>
                <Select
                  labelId="filter-select-id"
                  id="filter-id"
                  value={plotForPrimary}
                  label="Filter"
                  disabled={hydraulicsLanding?.loading}
                  onChange={(e) => setPlotForPrimary(e.target.value)}
                  sx={{
                    fontWeight: 'bolder',
                    fontSize: '18px',
                    background: 'unset',
                  }}
                >
                  {['Pressure', 'EMW'].map((item, idItem) => (
                    <MenuItem
                      key={`item_${item}_${idItem}`}
                      value={item}
                      sx={{ fontSize: '18px', fontWeight: 'bold' }}
                    >
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Show secondary depth filter only when EMW is selected */}
            {plotForPrimary === 'EMW' && (
              <>
                <Grid item>
                  <Typography sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                    at
                  </Typography>
                </Grid>

                <Grid item>
                  <FormControl
                    fullWidth
                    sx={{ bgcolor: 'transparent', width: '200px' }}
                  >
                    <InputLabel id="depth-input-label">Depth</InputLabel>
                    <Select
                      labelId="depth-select-id"
                      id="depth-id"
                      value={plotForSecondary}
                      label="Depth"
                      disabled={hydraulicsLanding?.loading}
                      onChange={(e) => setPlotForSecondary(e.target.value)}
                      sx={{
                        fontWeight: 'bolder',
                        fontSize: '18px',
                        background: 'unset',
                      }}
                    >
                      {secondaryDepthField.map((item, idItem) => (
                        <MenuItem
                          key={`item_${item}_${idItem}`}
                          value={item.label}
                          sx={{
                            color: item?.color,
                            fontSize: '18px',
                            fontWeight: 'bold',
                          }}
                        >
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
          </Box>
        </Grid>
      </Box>
    );
  };

  const ChartTableComponent = () => {
    if (isMobile) {
      return (
        <Drawer
          open={showMobileChartTable}
          onClose={() => setShowMobileChartTable(false)}
          sx={{
            height: '100vh',
          }}
          PaperProps={{ style: { position: 'absolute', width: '100%' } }}
          variant='temporary'
        >
          <TableFilterAndDetailsComponent />
          <Grid item xs={12}>
            {tableRow.map((row, index) => {
              const activeColumns =
                plotForPrimary === 'Pressure'
                  ? columns(displacementUnits, 'ECD', sbpField)
                  : customColumns(displacementUnits, secondaryField, sbpField);

              return (
                <Box key={index} className={classes.gridContainer}>
                  {activeColumns
                    .filter((col) => !col.hide)
                    .map((col, idx) => {
                      let value;

                      if (col?.renderCell) {
                        value = col.renderCell({
                          row,
                          field: col.field,
                          value: row[col.field],
                        });
                      } else if (col?.valueGetter) {
                        value = col.valueGetter({ row });
                      } else if (col?.valueFormatter) {
                        value = col.valueFormatter({
                          row,
                          field: col.field,
                          value: row[col.field],
                        });
                      } else {
                        value = row[col.field];
                      }

                      return (
                        <Grid
                          container
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                          key={idx}
                          className={classes.gridItem}
                        >
                          <Typography className={classes.headerName} variant="body1">
                            {col.headerName}
                          </Typography>
                          <Box sx={{ flex: 0.5 }}>{value}</Box>
                        </Grid>
                      );
                    })}
                </Box>
              );
            })}
          </Grid>
          <Fab size='small' className={classes.accordianScrollUp} onClick={handleScrollToTop}>
            <KeyboardArrowUp sx={{ fontSize: '30px' }} />
          </Fab>
        </Drawer>
      );
    }
    return (
      <Datagrid
        loading={hydraulicsLanding.loading}
        data={tableRow}
        sx={{
          '.MuiDataGrid-cell': {
            fontSize: '1rem',
            padding: '2px',
          },
          '.MuiDataGrid-columnHeader': {
            fontSize: '1.2rem',
            fontWeight: '900 !important',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            overflow: 'visible',
            lineHeight: '1.43rem',
            whiteSpace: 'normal',
          },
          '& .MuiDataGrid-footerContainer': {
            minHeight: '0px'
          },
        }}
        columns={plotForPrimary === 'Pressure' ? columns(displacementUnits, 'ECD', sbpField) : customColumns(displacementUnits, secondaryField, sbpField)}
        disableStickyHeader={true}
        autoHeight={false}
        height={'900px'}
        getRowHeight={() => 'auto'}
        pageSize={100}
        {...(tableRow.length <= 100
          ? { hideFooterPagination: true, hideFooterSelectedRowCount: true }
          : {})}
        toolbar={{
          options: {
            columns: false,
            filters: false,
            density: false,
            export: {
              enabled: true,
            }
          },
        }}
      />
    );
  };

  const handleScrollToTop = (event) => {
    const anchor = (event.target.ownerDocument || document).querySelector(
      '#back-to-top',
    );

    if (anchor) {
      anchor.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    }
  };

  if (!displacement?.results?.length) {
    return null;
  }

  if (isMobile) {
    if (!tableRow?.length) return null;
    return (
      <>
        {!showMobileChartTable && (
          <Grid item container sx={{ display: showMobileChartTable && 'none' }} xs={12} marginTop={'17px'} alignItems="center" justifyContent="center">
            {!hydraulicsLanding.loading && <>
              <Box display="flex" justifyContent="center" alignItems="center">
                <Grid item sx={{
                  fontSize: { xs: '18px', sm: '22px', md: '23px' },
                  whiteSpace: 'pre-line',
                  textAlign: 'center'
                }} className={classes.titleHeader}>
                  {titleHeader}
                </Grid>
              </Box></>}
            <Grid item xs={12}>
              <ChartComponent />
            </Grid>
            <Box className={classes.menuTogglerMobileBtn}>
              <IconButton
                onClick={() => {
                  setShowMobileChartTable(!showMobileChartTable);
                }}>
                <ChevronLeft sx={{ fontSize: '25px' }} />
              </IconButton>
            </Box>
          </Grid>
        )}
        {showMobileChartTable && (
          <Grid item container sx={{ display: !showMobileChartTable && 'none' }} xs={12} marginTop={'17px'} alignItems="center" justifyContent="center">
            {tableRow && <ChartTableComponent />}
            <Box className={classes.menuTogglerMobileTableBtn}>
              <IconButton
                onClick={() => {
                  setShowMobileChartTable(!showMobileChartTable);
                }}>
                <ChevronRight sx={{ fontSize: '25px' }} />
              </IconButton>
            </Box>
          </Grid>
        )}
      </>
    );
  }

  return (<>
    <Grid container item xs={showTable ? 7 : 12} style={{ marginTop: showTable ? '1.25%' : '2%' }} alignItems="center" justifyContent={'center'} >
      {!hydraulicsLanding.loading && <>
        <Box display="flex" justifyContent="center" alignItems="center">
          <Grid item className={classes.titleHeader}>
            {titleHeader}
          </Grid>
        </Box></>}
    </Grid>
    <Box className={classes.boxStyle}>
      <Grid container marginBottom={'2%'}>
        <Grid item xs={showTable ? 7 : 12} sx={{
          p: 2,
          pt: 10,
          '.canvasjs-chart-container canvas': {
            cursor: hydraulicsLanding?.loading ? 'wait !important' : ''
          }
        }}>
          {hydraulicsLanding.loading
            ? (
              <Box
                className={classes.progressBarStyle}
              >
                <CircularProgress />
              </Box>
              )
            : (
              <ChartComponent />
              )}
        </Grid>
        {!showTable && <Box className={classes.menuTogglerBtn}>
          <IconButton
            onClick={() => {
              setShowTable(!showTable);
            }}>
            <ChevronLeft className={classes.iconSize} />
          </IconButton>
        </Box>}
        {showTable && <Grid item xs={5} marginTop={'2.5%'}>
          <Box className={classes.menuTogglerTableBtn}>
            <IconButton
              onClick={() => {
                setShowTable(!showTable);
              }}>
              <ChevronRight className={classes.iconSize} />
            </IconButton>
          </Box>
          <TableFilterAndDetailsComponent />
          {tableRow && <Grid item xs={12}>
            <ChartTableComponent />
          </Grid>}
        </Grid>}
      </Grid>
    </Box>
  </>
  );
};
FeaturedWellDisplacementChart.propTypes = {
  data: PropTypes.any,
  units: PropTypes.any
};

export default FeaturedWellDisplacementChart;
